/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Routes, Route, Outlet, useParams } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";

import { usePatient } from "@/hooks/usePatient";
import { useAttendance } from "@/hooks/useAttendance";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useAuth } from "@/modules/auth";

import { ProfessionalsWithRoleContextProvider } from "@/contexts/ProfessionalWithRoleContext";
import { PartogramFetusContextProvider } from "@/contexts/PartogramFetusContext";

import { Preoperative } from "@/components/SurgicalCenter/SurgicalAttendance/Preoperative";
import { NursingAdmissionView } from "@/components/SurgicalCenter/SurgicalAttendance/Preoperative/NursingAdmissionView";
import { MedicalAdmissionView } from "@/components/SurgicalCenter/SurgicalAttendance/Preoperative/MedicalAdmissionView";
import { PreAnestheticConsultationView } from "@/components/SurgicalCenter/SurgicalAttendance/Preoperative/PreAnestheticConsultationView";

import { OperatingRoom } from "@/components/SurgicalCenter/SurgicalAttendance/OperatingRoom/index";
import { MedicalSurgicalActView } from "@/components/SurgicalCenter/SurgicalAttendance/OperatingRoom/MedicalSurgicalActView";

import { SafeSurgery } from "@/components/SurgicalCenter/SurgicalAttendance/SafeSurgery";
import { PreInductionAnesthesia } from "@/components/SurgicalCenter/SurgicalAttendance/SafeSurgery/PreInductionAnesthesia";
import { PreSurgeryForm } from "@/components/SurgicalCenter/SurgicalAttendance/SafeSurgery/PreSurgeryForm";
import { PreRecoveryRoomForm } from "@/components/SurgicalCenter/SurgicalAttendance/SafeSurgery/PreRecoveryRoomForm";
import { RecoveryRoomForm } from "@/components/SurgicalCenter/SurgicalAttendance/SafeSurgery/RecoveryRoomForm";

import { PatientProfile } from "@/components/Attendance/PatientProfile";
import { PageLink, PageTitle } from "@/../_metronic/layout/core";
import { Attendance } from "@/components/Attendance";
import { Portal } from "@/components/Portal";

export function AttendanceRoutes() {
	const [skeleton, setSkeleton] = useState(true);
	const [remainingTime, setRemainingTime] = useState(0);
	const { attendanceId } = useParams();
	const { setAttendanceId } = useAttendanceId();
	const { setPatient } = usePatient();
	const { attendance } = useAttendance();
	const { currentAuth } = useAuth();

	const { start: startIdleTimer, getRemainingTime } = useIdleTimer({
		startManually: true,
		timeout: 10000,
		throttle: 1000,
	});

	const profileBreadCrumbs: PageLink[] = [
		{
			title: "Leitos",
			path: "/centro-cirurgico",
			isSeparator: false,
			isActive: false,
		},
	];

	const operatingRoomPermissions = [
		"View-Surgical-Nursing-Act",
		"View-Surgical-Medical-Act",
		"View-Surgical-Anesthetic-Act",
	];

	const hasOperatingRoomPermissions = operatingRoomPermissions.some((permission) =>
		currentAuth?.user.can(permission as SystemPermissions)
	);

	useEffect(() => {
		if (Object.keys(attendance).length) {
			setSkeleton(false);
			setPatient(attendance.patient);
		}
	}, [attendance]);

	useEffect(() => {
		if (attendanceId) {
			setAttendanceId(Number(attendanceId));
		}
	}, [attendanceId]);

	useEffect(() => {
		const interval = setInterval(() => {
			setRemainingTime(Math.ceil(getRemainingTime() / 1000));
		}, 500);

		return () => clearInterval(interval);
	});

	useEffect(() => {
		startIdleTimer();
	}, []);

	return (
		<Routes>
			<Route
				element={
					<>
						<PatientProfile attendance={attendance} skeleton={skeleton} />
						<Attendance />
						<Outlet />
					</>
				}
			>
				{/* surgical center routes */}
				<Route
					path="pre-operatorio"
					element={
						<>
							<PageTitle breadcrumbs={profileBreadCrumbs}>Pré-Operatório</PageTitle>
							<Portal containerId="attendance-content">
								<Preoperative />
							</Portal>
						</>
					}
				>
					<Route
						path="admissao-de-enfermagem"
						element={<NursingAdmissionView attendance={attendance} />}
					/>

					<Route path="admissao-medica" element={<MedicalAdmissionView />} />

					<Route
						path="consulta-pre-anestesica"
						element={<PreAnestheticConsultationView />}
					/>
				</Route>

				{hasOperatingRoomPermissions && (
					<Route
						path="sala-de-operacao"
						element={
							<>
								<PageTitle breadcrumbs={profileBreadCrumbs}>
									Sala de Operação
								</PageTitle>
								<Portal containerId="attendance-content">
									<OperatingRoom />
								</Portal>
							</>
						}
					>
						{currentAuth?.user.can("View-Surgical-Nursing-Act") && (
							<Route
								path="ato-cirurgico-de-enfermagem"
								element={<h5 className="ms-5">Ato Cirúrgico de Enfermagem</h5>}
							/>
						)}

						{currentAuth?.user.can("View-Surgical-Medical-Act") && (
							<Route
								path="ato-cirurgico-medico"
								element={
									<PartogramFetusContextProvider>
										<MedicalSurgicalActView remainingTime={remainingTime} />
									</PartogramFetusContextProvider>
								}
							/>
						)}

						{currentAuth?.user.can("View-Surgical-Anesthetic-Act") && (
							<Route
								path="ato-cirurgico-anestesico"
								element={<h5 className="ms-5">Ato Cirúrgico Anestésico</h5>}
							/>
						)}
					</Route>
				)}

				<Route
					path="sala-de-recuperacao-pos-anestesica"
					element={
						<>
							<PageTitle breadcrumbs={profileBreadCrumbs}>
								Sala de Recuperação Pós-Anestésica
							</PageTitle>
							<Portal containerId="attendance-content">
								<></>
							</Portal>
						</>
					}
				/>

				<Route
					path="cirurgia-segura"
					element={
						<>
							<PageTitle breadcrumbs={profileBreadCrumbs}>Cirurgia Segura</PageTitle>
							<Portal containerId="attendance-content">
								<ProfessionalsWithRoleContextProvider
									roles={["Técnico de Enfermagem", "Enfermeiro"]}
								>
									<SafeSurgery />
								</ProfessionalsWithRoleContextProvider>
							</Portal>
						</>
					}
				>
					<Route
						path="pre-inducao-anestesica"
						element={<PreInductionAnesthesia remainingTime={remainingTime} />}
					/>

					<Route
						path="pre-cirurgia"
						element={
							<PreSurgeryForm
								surgicalAttendance={attendance.surgicalAttendance}
								remainingTime={remainingTime}
							/>
						}
					/>

					<Route
						path="pre-sala-de-recuperacao"
						element={
							<PreRecoveryRoomForm
								surgicalAttendance={attendance.surgicalAttendance}
								remainingTime={remainingTime}
							/>
						}
					/>

					<Route
						path="sala-de-recuperacao"
						element={
							<RecoveryRoomForm
								remainingTime={remainingTime}
								surgicalAttendance={attendance.surgicalAttendance}
							/>
						}
					/>
				</Route>
			</Route>
		</Routes>
	);
}
